import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, MoreHorizontal, Pencil, Plus, Search, Shield, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useStaff } from "@/context/StaffAuth";
import { api, errMsg } from "@/lib/api";
import { useDebounced } from "@/hooks/useFetch";
import { dateTime } from "@/lib/format";
import { EmptyState, ErrorState, Field, PageHeader, StatusBadge, TableSkeleton, rowCls, tdCls, thCls } from "@/components/admin/AdminUI";

const RoleChecks = ({ roles, value, onChange, idPrefix }) => (
  <ul className="grid sm:grid-cols-2 gap-2">
    {roles.map((r) => {
      const checked = value.includes(r.id);
      return (
        <li key={r.id} className={`flex items-start gap-3 rounded-lg border p-3 ${checked ? "bg-secondary/70" : ""}`}>
          <Checkbox id={`${idPrefix}-${r.id}`} checked={checked} onCheckedChange={(v) => onChange(v ? [...value, r.id] : value.filter((x) => x !== r.id))} data-testid={`${idPrefix}-role-${r.key}`} />
          <label htmlFor={`${idPrefix}-${r.id}`} className="cursor-pointer"><span className="block text-sm font-medium leading-none">{r.name}</span><span className="block text-xs text-muted-foreground mt-1">{r.description}</span></label>
        </li>
      );
    })}
  </ul>
);

export default function AdminUsers() {
  const { can, user: me } = useStaff();
  const manage = can("users.manage");
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [create, setCreate] = useState(false);
  const [cf, setCf] = useState({ email: "", name: "", password: "", role_ids: [] });
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ name: "", status: "active" });
  const [rolesFor, setRolesFor] = useState(null);
  const [rf, setRf] = useState([]);
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [u, r] = await Promise.all([api.get("/admin/users", { params: { q: dq } }), api.get("/admin/roles")]);
      setUsers(u.data.items || []);
      setRoles(r.data.items || []);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [dq]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Staff users · Admin"; }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      load();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageHeader eyebrow="Control" title="Staff users" description="Staff accounts and their role assignments. Access is deny-by-default and enforced server-side; at least one active administrator must always remain."
        actions={manage && <Button onClick={() => { setCf({ email: "", name: "", password: "", role_ids: [] }); setCreate(true); }} className="rounded-lg" data-testid="admin-users-new-button"><UserPlus className="h-4 w-4 mr-2" />Invite staff</Button>} />
      <div className="relative max-w-md mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 bg-card rounded-lg" data-testid="admin-users-search-input" />
      </div>
      <div className="bg-card border rounded-xl overflow-hidden">
        {error ? <ErrorState message={error} onRetry={load} /> : users === null ? <TableSkeleton cols={5} /> : users.length === 0 ? <EmptyState title="No staff found" /> : (
          <Table data-testid="admin-data-table">
            <TableHeader className="bg-secondary/60"><TableRow><TableHead className={thCls}>Staff member</TableHead><TableHead className={thCls}>Roles</TableHead><TableHead className={thCls}>Status</TableHead><TableHead className={thCls}>Last sign in</TableHead><TableHead className={thCls}>Created</TableHead>{manage && <TableHead className={thCls} />}</TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className={rowCls} data-testid="admin-user-row">
                  <TableCell className={tdCls}><p className="font-medium">{u.name}{u.id === me?.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">You</span>}</p><p className="text-xs text-muted-foreground">{u.email}</p></TableCell>
                  <TableCell className={tdCls}><div className="flex flex-wrap gap-1">{(u.roles || []).map((r) => <StatusBadge key={r.id} status={r.key} label={r.name} tone={r.key === "admin" ? "ink" : "neutral"} />)}{!u.roles?.length && <span className="text-xs text-muted-foreground">No roles</span>}</div></TableCell>
                  <TableCell className={tdCls}><StatusBadge status={u.status} /></TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{u.last_login_at ? dateTime(u.last_login_at) : "Never"}</TableCell>
                  <TableCell className={`${tdCls} text-muted-foreground`}>{dateTime(u.created_at)}</TableCell>
                  {manage && (
                    <TableCell className={`${tdCls} text-right`}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" aria-label="User actions" data-testid="admin-user-menu"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => { setEf({ name: u.name, status: u.status }); setEdit(u); }} data-testid="admin-user-edit-action"><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => { setRf((u.roles || []).map((r) => r.id)); setRolesFor(u); }} data-testid="admin-user-roles-action"><Shield className="h-4 w-4 mr-2" />Roles</DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => { setPw(""); setPwFor(u); }} data-testid="admin-user-password-action"><KeyRound className="h-4 w-4 mr-2" />Reset password</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-user-create-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">Invite staff member</DialogTitle><DialogDescription>Passwords need 12+ characters with upper, lower, digit and symbol. Share the temporary password securely.</DialogDescription></DialogHeader>
          <form onSubmit={async (e) => { e.preventDefault(); if (await run(() => api.post("/admin/users", cf), "Staff user created")) setCreate(false); }} className="space-y-4 pt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" htmlFor="nu-name" required><Input id="nu-name" required value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} data-testid="admin-user-name-input" /></Field>
              <Field label="Work email" htmlFor="nu-email" required><Input id="nu-email" type="email" required value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} data-testid="admin-user-email-input" /></Field>
              <Field label="Temporary password" htmlFor="nu-pw" required className="sm:col-span-2"><Input id="nu-pw" type="password" required minLength={12} value={cf.password} onChange={(e) => setCf({ ...cf, password: e.target.value })} autoComplete="new-password" data-testid="admin-user-password-input" /></Field>
            </div>
            <Field label="Roles"><RoleChecks roles={roles} value={cf.role_ids} onChange={(v) => setCf({ ...cf, role_ids: v })} idPrefix="nu" /></Field>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setCreate(false)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="admin-user-create-submit"><Plus className="h-4 w-4 mr-1.5" />{busy ? "Creating…" : "Create user"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="admin-user-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">Edit {edit?.email}</DialogTitle><DialogDescription>Disabling a user revokes all of their sessions immediately.</DialogDescription></DialogHeader>
          <form onSubmit={async (e) => { e.preventDefault(); if (await run(() => api.put(`/admin/users/${edit.id}`, ef), "User updated")) setEdit(null); }} className="space-y-4 pt-2">
            <Field label="Full name" htmlFor="eu-name" required><Input id="eu-name" required value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="admin-user-edit-name-input" /></Field>
            <Field label="Status" htmlFor="eu-status">
              <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                <SelectTrigger id="eu-status" data-testid="admin-user-edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="disabled">Disabled</SelectItem></SelectContent>
              </Select>
            </Field>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="admin-user-edit-submit">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rolesFor} onOpenChange={(o) => !o && setRolesFor(null)}>
        <DialogContent className="max-w-2xl" data-testid="admin-user-roles-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">Roles for {rolesFor?.name}</DialogTitle><DialogDescription>Permissions are the union of all assigned roles. Only administrators can grant the Administrator role.</DialogDescription></DialogHeader>
          <div className="pt-2"><RoleChecks roles={roles} value={rf} onChange={setRf} idPrefix="ur" /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={() => setRolesFor(null)}>Cancel</Button><Button disabled={busy} onClick={async () => { if (await run(() => api.put(`/admin/users/${rolesFor.id}/roles`, { role_ids: rf }), "Roles updated")) setRolesFor(null); }} data-testid="admin-user-roles-submit">{busy ? "Saving…" : "Save roles"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent data-testid="admin-user-password-dialog">
          <DialogHeader><DialogTitle className="font-display text-3xl font-medium">Reset password</DialogTitle><DialogDescription>Sets a new password for {pwFor?.email} and signs them out everywhere.</DialogDescription></DialogHeader>
          <form onSubmit={async (e) => { e.preventDefault(); if (await run(() => api.post(`/admin/users/${pwFor.id}/reset-password`, { new_password: pw }), "Password reset")) setPwFor(null); }} className="space-y-4 pt-2">
            <Field label="New password" htmlFor="rp-pw" required hint="12+ characters incl. upper, lower, digit and symbol"><Input id="rp-pw" type="password" required minLength={12} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="admin-user-newpassword-input" /></Field>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setPwFor(null)}>Cancel</Button><Button type="submit" disabled={busy} data-testid="admin-user-password-submit">{busy ? "Resetting…" : "Reset password"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
